/* What every page calls itself: the tab title, the search-result snippet, and
   the link preview. One table, read by both sides (2 Aug 2026, Rob: "fix all
   the page tags and metadata").
//
   Until today the whole SPA was one document called "Practice" with no
   description, no canonical, and no og tags at all. Two costs, and neither is
   hypothetical:

   - Search. /welcome is the sitemap's priority 1.0 entry and the target of
     every call to action on the public question pages, and it was served to a
     crawler as a titleless shell. The pages we most want people to arrive at
     had nothing for a result to say.
   - The browser. Six tabs of the app were six tabs called "Practice", and the
     back-button history was a list of identical entries.

   The route table lives here rather than in the client store because the
   server answers the first request for a client route and has to put the same
   words in the head that the client will put in the title bar a moment later.
   Keyed by screen id, not by path: "/" is the front door to a visitor and
   Today to somebody signed in, and only the client knows which.

   Descriptions are held under about 155 characters, which is where Google
   truncates, and they describe the page rather than the product: a set of
   pages carrying one paragraph about the company is a set of pages with no
   description at all. */

export const SITE_NAME = "Practice by Lab 10";
// 1200x630 JPEG, built by scripts/social-card.ts. JPEG and not the WebP the
// rest of the product uses, because Facebook and LinkedIn still will not
// render a WebP preview.
export const SOCIAL_CARD = "/social/practice.jpg";
export const SOCIAL_CARD_ALT =
  "Practice by Lab 10, over a photograph of a quiet coast at dusk: practice what they preach.";
// The card's own sub-line, kept here so the picture and the description that
// travels beside it cannot drift apart. scripts/social-card.ts reads it.
export const SOCIAL_CARD_LINE = "Build a daily practice from the experts you already trust.";

export interface PageMeta {
  // Without the site name, which every consumer of this adds itself.
  title: string;
  desc: string;
  /* Whether a crawler may index this URL. False for everything the server
     cannot render: the app's own screens are behind a session and would be
     indexed as the same empty document under twenty URLs. The front door is
     the exception on purpose, and the legal and company pages are a
     deliberate no for now, since their words exist only once React has run. */
  index?: boolean;
  // When this page is a second address for another one. "/" and /welcome are
  // the same page to anyone who is not signed in.
  canonicalOf?: string;
}

/* The words a stranger meets first, so they say what they get rather than how
   it is made (2 Aug 2026, Rob: "every claim graded and linked to its source is
   pretty weak marketing since nobody will understand what that is. We should
   say something about building your daily practice"). The grading and the
   source links are the best thing about the product and they are the answer to
   a question nobody has asked yet; they belong on the page, not in the
   result. */
const FRONT: PageMeta = {
  title: "Build your daily practice from the experts you trust",
  desc: "Follow the health experts you trust and build a daily practice out of what they actually publish. A few things to decide on, at your own pace.",
  index: true,
};

export const PAGE_META: Record<string, PageMeta> = {
  welcome: FRONT,
  // The same page to a visitor, Today to an account. Indexable, pointing its
  // canonical at /welcome so the two do not compete as duplicates.
  today: { ...FRONT, canonicalOf: "welcome" },
  signin: {
    title: "Sign in",
    desc: "Sign in to Practice with a link sent to your email. There is no password.",
  },
  "onb-areas": {
    title: "Get started",
    desc: "Pick the areas you want to work on, and Practice suggests the experts worth following in them.",
  },
  "onb-experts": {
    title: "Choose who to follow",
    desc: "Follow the experts you trust. Everything they publish becomes practices you can decide on, one at a time.",
  },
  "onb-email": {
    title: "Create your account",
    desc: "Your email is your login. Practice sends a link, you tap it, and your practice is waiting.",
  },
  about: {
    title: "About Practice",
    desc: "Who makes Practice, how the grading works, and what we will and will not do with what the experts publish.",
  },
  contact: {
    title: "Contact",
    desc: "How to reach Lab 10 about Practice: corrections, rights notices, and everything else.",
  },
  privacy: {
    title: "Privacy Policy",
    desc: "What Practice collects, why, who it goes to, and how to get it back or have it deleted.",
  },
  terms: {
    title: "Terms of Use",
    desc: "The terms you accept when you use Practice, including what it is not: medical care.",
  },
  health: {
    title: "Consumer Health Data Privacy Policy",
    desc: "The separate policy Washington's My Health My Data Act requires: what health data Practice holds, and your rights over it.",
  },
  practices: { title: "My Practice", desc: "" },
  discover: { title: "Discover", desc: "" },
  crossfire: { title: "Crossfire", desc: "" },
  feed: { title: "Feed", desc: "" },
  follow: { title: "Follow", desc: "" },
  expert: { title: "Profile", desc: "" },
  controls: { title: "Topic controls", desc: "" },
  review: { title: "Weekly review", desc: "" },
  program: { title: "Programs", desc: "" },
};

// What "/" calls itself once there is a session behind it.
export const TODAY_TITLE = "Today";

/* The tab title: the page, then the site. An unknown screen gets the site
   name alone rather than a guess. */
export function titleFor(screen: string): string {
  const meta = PAGE_META[screen];
  return meta ? `${meta.title} | ${SITE_NAME}` : SITE_NAME;
}

/* The title the CLIENT shows once it knows who is looking. "/" is the front
   door to a visitor and Today to an account, and the server has to answer the
   request before it can tell the two apart, so it always sends the front
   door's words and the client corrects them on mount. */
export function clientTitleFor(screen: string, signedIn: boolean): string {
  if (screen === "today" && signedIn) return `${TODAY_TITLE} | ${SITE_NAME}`;
  return titleFor(screen);
}
